import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ApiError, type ApiErrorBody } from '../api';
import { uploadResultText } from './format';
import { BANK_KEY, type UploadResult } from './types';
import { FileDrop } from '../ui/FileDrop';

/** The server's limits: 20 files in one upload, 10 MB a file. */
const MAX_FILES = 20;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.xml,.sta,.mt940,.txt,.940,application/xml,text/xml,text/plain';

/** Sends statement files to POST /api/bank/statements (multipart, field "files"); the answer has one result per file. */
async function uploadStatements(files: File[]): Promise<UploadResult[]> {
  const body = new FormData();
  for (const f of files) body.append('files', f, f.name);
  let response: Response;
  try {
    response = await fetch('/api/bank/statements', { method: 'POST', credentials: 'include', body });
  } catch {
    throw new ApiError(0, 'network', 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.');
  }
  let parsed: (ApiErrorBody & { results?: UploadResult[] }) | null = null;
  try {
    parsed = (await response.json()) as ApiErrorBody & { results?: UploadResult[] };
  } catch {
    // not JSON
  }
  if (response.ok && parsed?.results) return parsed.results;
  if (response.status === 413) throw new ApiError(413, 'payload_too_large', 'Plik jest za duży (limit 10 MB)');
  throw new ApiError(response.status, parsed?.error ?? 'unknown', parsed?.message ?? 'Serwer nie odpowiedział poprawnie. Spróbuj ponownie za chwilę.', parsed?.details);
}

/**
 * "Wgraj wyciąg" (F37): the screen's main button; picks one or more files (XML camt.053 or MT940). The page shows the
 * answer (`onResult`): per file how many operations were read, "już wczytany" or why it failed.
 */
export function UploadStatements({ onResult }: { onResult: (r: { results: UploadResult[]; error: string }) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);

  async function send(list: FileList | File[] | null) {
    const files = [...(list ?? [])];
    if (input.current) input.current.value = ''; // the same file may be picked again
    if (files.length === 0) return;
    if (files.length > MAX_FILES) return onResult({ results: [], error: `Najwyżej ${MAX_FILES} plików naraz` });
    const big = files.find((f) => f.size > MAX_BYTES);
    if (big) return onResult({ results: [], error: `Plik „${big.name}” jest za duży (limit 10 MB)` });
    onResult({ results: [], error: '' });
    setPending(true);
    try {
      const results = await uploadStatements(files);
      onResult({ results, error: '' });
      await queryClient.invalidateQueries({ queryKey: BANK_KEY });
    } catch (e) {
      onResult({ results: [], error: e instanceof ApiError ? e.message : 'Nie udało się wgrać plików' });
    } finally {
      setPending(false);
    }
  }

  return (
    <FileDrop multiple accept={ACCEPT} disabled={pending} onFiles={(files) => void send(files)}>
      <input ref={input} type="file" multiple accept={ACCEPT} hidden aria-label="Pliki wyciągów" onChange={(e) => void send(e.target.files)} />
      <button className="btn primary" type="button" data-feature-id="bank.upload" disabled={pending} onClick={() => input.current?.click()}>
        {pending ? 'Wczytywanie…' : 'Wgraj wyciąg'}
      </button>
    </FileDrop>
  );
}

/** The answer of the last upload, one line per file. */
export function UploadResults({ results, error }: { results: UploadResult[]; error: string }) {
  if (error) {
    return (
      <p className="alert" role="alert">
        {error}
      </p>
    );
  }
  if (results.length === 0) return null;
  return (
    <ul className="bank-upload-results" aria-label="Wynik wgrywania">
      {results.map((r, i) => (
        <li key={`${r.filename}-${i}`} className={r.status === 'error' ? 'is-error' : r.status === 'imported' ? 'is-ok' : undefined}>
          <strong>{r.filename}</strong>: {uploadResultText(r)}
        </li>
      ))}
    </ul>
  );
}
